/**
 * Main notification manager - coordinates toast and panel managers
 */

import { ToastManager } from './toast-manager.js';
import { PanelManager } from './panel-manager.js';

export class NotificationManager {
    constructor() {
        this.toastManager = new ToastManager();
        this.panelManager = new PanelManager();
        this.unreadCount = 0;
        this.updateInterval = null;
        this.pollIntervalMs = 0;
        this.lastPollAt = 0;
        this._onVisibilityChange = null;
        this.shownNotificationIds = new Set(); // Track already shown notifications
        this.pendingActions = new Set(); // Track pending actions for idempotent execution
        this.actionDebounceTimer = null;
        this.lastActionExecutionTime = {}; // Track when each action was last executed
        this.consecutiveErrors = 0; // Track consecutive API errors
        this.isBlocked = false; // Block API requests after 3 consecutive errors
    }
    
    /**
     * Initialize notification system
     */
    async init() {
        try {
            // Initialize managers
            this.toastManager.init();
            this.panelManager.init();
            
            // Load toast notifications with count (unified request)
            await this.loadToastNotifications();
            
            // ✅ Update component status after notifications are loaded (simplified loader architecture)
            if(window.ComponentLoadingStatus){
                let caption = 'Notifications loaded';
                if (window.TranslationRegistry && typeof window.TranslationRegistry.translate === 'function') {
                  const lang = window.LanguageManager ? window.LanguageManager.getCurrentLanguage() : (window.LANG || 'EN');
                  caption = window.TranslationRegistry.translate('loader_stage_notifications_loaded', lang);
                }
                window.ComponentLoadingStatus.update('ajaxNotifications', 'ready', caption);
            }
            
            // Setup visibility-aware periodic polling for new notifications
            // Prod: 5min, Dev: 15s; paused while the tab is hidden, resumed on return
            this.pollIntervalMs = (window.SERVER_ENV && window.SERVER_ENV.isProd) ? 300000 : 15000;
            this.lastPollAt = Date.now();
            this._onVisibilityChange = () => this.handleVisibilityChange();
            document.addEventListener('visibilitychange', this._onVisibilityChange);
            if (!document.hidden) {
                this.startPolling();
            }
            
            // Setup menu click handler
            const menuItem = document.querySelector('[data-notifications-toggle]');
            if (menuItem) {
                menuItem.addEventListener('click', (e) => {
                    e.preventDefault();
                    if (debugMode === '1') console.log('[NotificationManager] Menu item clicked');
                    this.panelManager.open();
                });
            } else {
                console.warn('[NotificationManager] Menu item not found!');
            }
            
        } catch (error) {
            console.error('[NotificationManager] Init failed:', error);
        }
    }
    
    /**
     * Check if API requests are blocked
     */
    _checkBlocked() {
        if (this.isBlocked) {
            console.warn('[NotificationManager] API requests blocked due to 3 consecutive errors. Reload page to retry.');
            return true;
        }
        return false;
    }
    
    /**
     * Handle API error - increment counter and block if needed
     */
    _handleApiError() {
        this.consecutiveErrors++;
        if (this.consecutiveErrors >= 3) {
            this.isBlocked = true;
            console.error('[NotificationManager] Blocking API requests after 3 consecutive errors. Please reload the page.');
        }
    }
    
    /**
     * Handle API success - reset error counter
     */
    _handleApiSuccess() {
        if (this.consecutiveErrors > 0) {
            this.consecutiveErrors = 0;
        }
    }
    
    /**
     * Update badge counter
     * @param {number} count - Count to set (required, no separate API call)
     */
    async updateBadge(count) {
        if (count === null || count === undefined) {
            console.warn('[NotificationManager] updateBadge() called without count parameter. Use unified API requests.');
            return;
        }
        
        try {
            this.unreadCount = count;
            
            const badge = document.querySelector('[data-notifications-count]');
            if (badge) {
                badge.textContent = this.unreadCount;
                badge.classList.toggle('d-none', this.unreadCount === 0);
            }
            
            this._handleApiSuccess();
        } catch (error) {
            console.error('[NotificationManager] Failed to update badge:', error);
            this._handleApiError();
        }
    }
    
    /**
     * Load toast notifications with badge count (unified request)
     */
    async loadToastNotifications() {
        if (this._checkBlocked()) return;
        
        try {
            // Use unified request to get both notifications and count
            const response = await fetch('/api/notifications?limit=3&include_count=true');
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }
            const data = await response.json();
            
            // Extract notifications and count from response
            const responseData = data.data || data;
            const notifications = responseData.notifications || [];
            const unreadCount = responseData.total_count !== undefined ? responseData.total_count : null;
            

            
            // Update badge with count from response
            if (unreadCount !== null) {
                await this.updateBadge(unreadCount);
            }
            
            notifications.forEach(notif => {
                this.toastManager.show(notif);
                this.shownNotificationIds.add(notif.id);
            });
            
            this._handleApiSuccess();
        } catch (error) {
            console.error('[NotificationManager] Failed to load toast notifications:', error);
            this._handleApiError();
        }
    }
    
    /**
     * Poll for new notifications
     * Uses single API request with include_count=true to get both notifications and badge count
     */
    async pollForNewNotifications() {
        if (this._checkBlocked()) return;
        
        try {
            if (debugMode === '1') console.log('[NotificationManager] Polling for new notifications...');
            
            // Single request to get both notifications and unread count
            const response = await fetch('/api/notifications?limit=10&include_count=true');
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }
            const data = await response.json();
            
            // Extract notifications and count from response
            const responseData = data.data || data;
            const notifications = responseData.notifications || [];
            const unreadCount = responseData.total_count !== undefined ? responseData.total_count : null;
            
            // Update badge with count from response
            if (unreadCount !== null) {
                await this.updateBadge(unreadCount);
            }
            
            // Show only new personal notifications
            notifications.forEach(notif => {
                if (!this.shownNotificationIds.has(notif.id)) {
                    // Only show personal notifications (user_id > 0)
                    if (notif.user_id > 0) {
                        if (debugMode === '1') console.log('[NotificationManager] New personal notification:', notif.id);
                        this.toastManager.show(notif);
                        this.shownNotificationIds.add(notif.id);
                    }
                }
            });
            
            this._handleApiSuccess();
        } catch (error) {
            console.error('[NotificationManager] Failed to poll notifications:', error);
            this._handleApiError();
        }
    }
    
    /**
     * Dismiss personal notification
     */
    async dismiss(notificationId) {
        if (this._checkBlocked()) return;
        
        try {
            if (debugMode === '1') console.log('[NotificationManager] Dismissing notification:', notificationId);
            const response = await fetch(`/api/notifications/${notificationId}/dismiss`, {
                method: 'POST'
            });
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }
            if (debugMode === '1') console.log('[NotificationManager] Dismiss response:', response.status);
            
            // Update badge locally (decrease by 1) instead of separate API call
            if (this.unreadCount > 0) {
                this.unreadCount--;
                await this.updateBadge(this.unreadCount);
            }
            
            this._handleApiSuccess();
        } catch (error) {
            console.error('[NotificationManager] Failed to dismiss:', error);
            this._handleApiError();
        }
    }
    
    /**
     * Register notification action (idempotent)
     * Actions are debounced and deduplicated via Set to prevent multiple executions
     */
    registerAction(actionType, notificationDate) {
        if (debugMode === '1') console.log('[NotificationManager] Registering action:', actionType, 'notif date:', notificationDate);
        
        // Check if this action was already executed after the notification was created
        if (notificationDate && this.lastActionExecutionTime[actionType]) {
            // Parse SQLite datetime string as UTC
            // SQLite stores dates as 'YYYY-MM-DD HH:MM:SS' in UTC
            const notifTimestamp = this._parseSqliteDatetime(notificationDate);
            const lastExecutionTime = this.lastActionExecutionTime[actionType];
            
            if (debugMode === '1') console.log('[NotificationManager] Comparing:', {
                notifTimestamp,
                lastExecutionTime,
                notifDate: new Date(notifTimestamp).toISOString(),
                lastExecutionDate: new Date(lastExecutionTime).toISOString()
            });
            
            if (notifTimestamp < lastExecutionTime) {
                if (debugMode === '1') console.log('[NotificationManager] Skipping action - already executed after notification');
                return;
            }
        }
        
        // Add to pending actions
        this.pendingActions.add(actionType);
        
        // Debounce execution
        if (this.actionDebounceTimer) {
            clearTimeout(this.actionDebounceTimer);
        }
        
        this.actionDebounceTimer = setTimeout(() => {
            this.executePendingActions();
        }, 1000); // 1 second debounce
    }
    
    /**
     * Execute all pending actions idempotently
     */
    async executePendingActions() {
        if (this.pendingActions.size === 0) return;
        
        if (debugMode === '1') console.log('[NotificationManager] Executing pending actions:', Array.from(this.pendingActions));
        
        const actionsToExecute = Array.from(this.pendingActions);
        this.pendingActions.clear();
        
        for (const action of actionsToExecute) {
            try {
                await this.executeAction(action);
            } catch (error) {
                console.error('[NotificationManager] Failed to execute action:', action, error);
            }
        }
    }
    
    /**
     * Execute specific action
     */
    async executeAction(actionType) {
        switch (actionType) {
            case 'refresh_files':
                if (window.refreshFilesFromServer) {
                    if (debugMode === '1') console.log('[NotificationManager] Refreshing files list');
                    await window.refreshFilesFromServer();
                    // Save execution timestamp for this action
                    this.lastActionExecutionTime[actionType] = Date.now();
                    if (debugMode === '1') console.log('[NotificationManager] Saved execution time for', actionType, ':', new Date(this.lastActionExecutionTime[actionType]).toISOString());
                }
                break;
            default:
                console.warn('[NotificationManager] Unknown action type:', actionType);
        }
    }
    
    /**
     * Parse SQLite datetime string as UTC timestamp
     * SQLite stores dates as 'YYYY-MM-DD HH:MM:SS' in UTC
     */
    _parseSqliteDatetime(datetimeStr) {
        // SQLite format: '2025-10-26 20:32:06'
        // Parse as UTC by appending 'Z' to force UTC timezone
        const parts = datetimeStr.split(' ');
        if (parts.length === 2) {
            // '2025-10-26 20:32:06' -> '2025-10-26T20:32:06Z'
            const isoString = `${parts[0]}T${parts[1]}Z`;
            return new Date(isoString).getTime();
        }
        // Fallback to regular parsing
        return new Date(datetimeStr).getTime();
    }
    
    /**
     * Start periodic polling (idempotent)
     */
    startPolling() {
        this.stopPolling();
        this.updateInterval = setInterval(() => {
            this.lastPollAt = Date.now();
            this.pollForNewNotifications();
        }, this.pollIntervalMs);
    }

    /**
     * Stop periodic polling
     */
    stopPolling() {
        if (this.updateInterval) {
            clearInterval(this.updateInterval);
            this.updateInterval = null;
        }
    }

    /**
     * Pause polling while tab is hidden; catch-up poll and resume on return
     */
    handleVisibilityChange() {
        if (document.hidden) {
            this.stopPolling();
            return;
        }
        // Overdue after background time -> fetch immediately so the user sees fresh state
        if (Date.now() - this.lastPollAt >= this.pollIntervalMs) {
            this.lastPollAt = Date.now();
            this.pollForNewNotifications();
        }
        this.startPolling();
    }

    /**
     * Cleanup
     */
    destroy() {
        this.stopPolling();
        if (this._onVisibilityChange) {
            document.removeEventListener('visibilitychange', this._onVisibilityChange);
            this._onVisibilityChange = null;
        }
        this.toastManager.destroy();
        this.panelManager.destroy();
    }
}

// Global instance
window.NotificationManager = new NotificationManager();

